/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
// ============================================================================
// ComponentSceneManagerThree.tsx
// ============================================================================

/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box3,
  Color,
  DirectionalLight,
  DoubleSide,
  FrontSide,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  ShadowMaterial,
  Vector3 as ThreeVector3,
  WebGLRenderer,
  AmbientLight,
  HemisphereLight,
} from 'three';

import { Vector3 as BabylonVector3 } from '@babylonjs/core';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { ThreeComponentScene, ThreeComponentSceneReadyArgs } from './ThreeScene';
import { ObjectLoaderThree, ObjectPartThree } from './ThreeObjectLoader';

import { ModelIlluminationData } from '../modelIllumination/modelIlluminationData';
import { SLTXFile } from '../fileFormats/sltx/sltxFile';
import { TreeOptionSelection, TreeOptionSelections } from '../modelConfiguration/data/optionsSelection';
import { FilePaths } from '../fileSystem/filePaths';
import { ModelGroupInfo } from '../fileFormats/sltx/modelGroupInfo';
import { SceneLightData } from '../modelIllumination/sceneLightData';
import { Vector } from '../types/vector/vector';

export type FncGetModelIlluminationData = () => ModelIlluminationData | undefined;
export type FncRegisterFncGetModelIlluminationData = (
  fnc: FncGetModelIlluminationData
) => void;

type ThreeeComponentSceneManagerParameters = {
  sltxFile: SLTXFile;
  composition: TreeOptionSelections;

  // Kept for API compatibility with the Babylon component.
  // Currently ignored in this Three.js version, as requested.
  skyboxPath: string;

  modelIlluminationData: ModelIlluminationData;

  // Kept for API compatibility.
  // Light-edit UI is not ported in this first version.
  modelIlluminationEditable: boolean;

  screenshotId: string | undefined;
  cameraPosition: BabylonVector3;
  setScreenshotHandler: (fileId: string, fileData: string) => void;
  registerCallbackGetModelIlluminationData: FncRegisterFncGetModelIlluminationData | undefined;
  setIsLoaded: (loaded: boolean) => void;
  setIsLoadingPart: (loaded: boolean) => void;
};

type DeferredObjectLoad = {
  groupId: number;
  optionId: number;
  loadObjectPromise: () => Promise<ObjectPartThree>;
};

const toThreeCameraDirection = (v: BabylonVector3): ThreeVector3 => new ThreeVector3(-v.x, v.y, v.z);

export function ThreeComponentSceneManager(
  {
    sltxFile,
    composition,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    skyboxPath,
    modelIlluminationData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    modelIlluminationEditable,
    screenshotId,
    cameraPosition,
    setScreenshotHandler,
    registerCallbackGetModelIlluminationData,
    setIsLoaded,
    setIsLoadingPart,
  }: ThreeeComponentSceneManagerParameters,
): JSX.Element {
  const hasInitialCameraFitRef = useRef<boolean>(false);
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const invalidateRenderRef = useRef<() => void>(() => {});

  const [sceneObjects, setSceneObjects] = useState<ObjectPartThree[]>([]);
  const [objectsToLoad, setObjectsToLoad] = useState<DeferredObjectLoad[]>([]);
  const [previousSelection, setPreviousSelection] = useState<TreeOptionSelections>();

  const sceneObjectsRef = useRef<ObjectPartThree[]>([]);
  const helperObjectsRef = useRef<Object3D[]>([]);
  const groundRef = useRef<Mesh | null>(null);
  const lightsRef = useRef<DirectionalLight[]>([]);

  const [windowSize, setWindowSize] = useState({ width: 160, height: 90 });
  const windowSizeRef = useRef(windowSize);

  const sceneContainerRef = useRef<HTMLDivElement | null>(null);

  const [actualScreenShotId, setActualScreenShotId] = useState<string | undefined>();
  const [secondaryScreenShotId, setSecondaryScreenShotId] = useState<string | undefined>();

  const invalidateNowAndNextFrame = () => {
    invalidateRenderRef.current();
    requestAnimationFrame(() => invalidateRenderRef.current());
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const { width, height } = entries[0].contentRect;
        setWindowSize({ width, height });
      }
    });

    if (sceneContainerRef.current) {
      resizeObserver.observe(sceneContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    windowSizeRef.current = windowSize;
  }, [windowSize]);

  useEffect(() => {
    sceneObjectsRef.current = sceneObjects;
  }, [sceneObjects]);

  const clearHelperObjects = () => {
    helperObjectsRef.current.forEach((obj) => {
      obj.parent?.remove(obj);
      disposeObject3D(obj);
    });

    helperObjectsRef.current = [];
    groundRef.current = null;
  };

  useEffect(() => () => clearHelperObjects(), []);

  const setupLightSources = (targetScene: Scene): DirectionalLight[] => {

    const lights: DirectionalLight[] = [];

    targetScene.background = new Color(0xffffff);

    const ambientLight = new AmbientLight(0xffffff, 0.32);
    targetScene.add(ambientLight);

    const hemiLight = new HemisphereLight(0xffffff, 0xffffff, 0.9);
    hemiLight.position.set(0, 10, 0);
    targetScene.add(hemiLight);

    const createLight = (
      name: string,
      position: ThreeVector3,
      direction: ThreeVector3,
      intensity: number,
      castShadow: boolean,
    ) => {
      const light = new DirectionalLight(
        new Color(1, 1, 1),
        intensity,
      );

      light.name = name;
      light.position.copy(position);

      light.target.position.copy(
        position.clone().add(direction.clone().normalize()),
      );

      light.castShadow = castShadow;

      if (castShadow) {
        light.shadow.mapSize.width = 1024;
        light.shadow.mapSize.height = 1024;
        light.shadow.bias = -0.0001;
        light.shadow.normalBias = 0.02;
        light.shadow.radius = 2;
      }

      targetScene.add(light);
      targetScene.add(light.target);
      lights.push(light);
    };

    modelIlluminationData.lights.forEach((lightObject) => {
      createLight(
        lightObject.name,
        new ThreeVector3(
          lightObject.position.x,
          lightObject.position.y,
          lightObject.position.z,
        ),
        new ThreeVector3(
          lightObject.direction.x,
          lightObject.direction.y,
          lightObject.direction.z,
        ),
        lightObject.intensity,
        lightObject.castShadows,
      );
    });

    createLight(
      'BottomFill',
      new ThreeVector3(0, -10, 0),
      new ThreeVector3(0, 1, 0),
      1.5,
      false,
    );

    createLight(
      'FrontTopLeft',
      new ThreeVector3(-10, 10, -10),
      new ThreeVector3(1, -1, 1),
      1.2,
      true,
    );

    lightsRef.current = lights;
    return lights;
  };

  const setupLightsPosition = (
    objectParts: ObjectPartThree[],
  ): void => {
    const box = getObjectsBox(objectParts.map((p) => p.root));
    if (!box) return;

    const center = new ThreeVector3();
    const size = new ThreeVector3();

    box.getCenter(center);
    box.getSize(size);

    lightsRef.current.forEach((light) => {
      const originalDirection = light.userData.originalDirection as ThreeVector3 | undefined;

      const direction = originalDirection
        ? originalDirection.clone().normalize()
        : new ThreeVector3(0, -1, 0);

      const invDirection = direction.clone().negate();

      invDirection.x *= 3.8;
      invDirection.z *= 1.8;
      invDirection.y *= 0.6;

      invDirection.normalize();

      const distanceFactor = 0.5;

      light.position.copy(
        center
          .clone()
          .add(new ThreeVector3(
            invDirection.x * size.x,
            invDirection.y * size.y,
            invDirection.z * size.z,
          ))
          .add(invDirection.clone().multiplyScalar(distanceFactor)),
      );

      light.target.position.copy(center);
      light.target.updateMatrixWorld();

      if (light.castShadow) {
        const refValue = Math.max(size.x, size.y, size.z, 1);
        const shadowCamera = light.shadow.camera;

        shadowCamera.left = -refValue * 2;
        shadowCamera.right = refValue * 2;
        shadowCamera.top = refValue * 2;
        shadowCamera.bottom = -refValue * 2;
        shadowCamera.near = 0.01;
        shadowCamera.far = refValue * 10;
        shadowCamera.updateProjectionMatrix();

        light.shadow.needsUpdate = true;
      }
    });
  };

  const setupCameraPositionAndMovement = (
    sceneObjectsToConsider: ObjectPartThree[],
    cameraPos: BabylonVector3,
  ): void => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const renderer = rendererRef.current;

    if (!camera || !controls || !renderer) return;

    fitCameraToObjects(
      camera,
      controls,
      sceneObjectsToConsider.map((p) => p.root),
      toThreeCameraDirection(cameraPos),
      windowSizeRef.current.width,
      windowSizeRef.current.height,
    );

    invalidateNowAndNextFrame();
  };

  const createReusableGroundMesh = (): Mesh => {
    const geometry = new PlaneGeometry(1, 1, 1, 1);
    geometry.rotateX(-Math.PI / 2);

    const material = new MeshStandardMaterial({
      color: new Color(0.9, 0.9, 0.9),
      roughness: 1.0,
      metalness: 0.0,
      side: FrontSide,
    });

    const ground = new Mesh(geometry, material);
    ground.name = 'myGround';
    ground.castShadow = false;
    ground.receiveShadow = true;

    return ground;
  };

  const updateReusableGroundMesh = (
    ground: Mesh,
    objectParts: ObjectPartThree[],
  ): boolean => {
    const box = getObjectsBox(objectParts.map((p) => p.root));
    if (!box) return false;

    const center = new ThreeVector3();
    const size = new ThreeVector3();

    box.getCenter(center);
    box.getSize(size);

    const largestSide = Math.max(size.x, size.z, 1);
    const groundSide = largestSide * 70;

    const oldGeometry = ground.geometry;
    const newGeometry = new PlaneGeometry(groundSide, groundSide, 32, 32);
    newGeometry.rotateX(-Math.PI / 2);

    ground.geometry = newGeometry;
    oldGeometry.dispose();

    ground.position.copy(center);
    ground.position.y -= size.y / 2.0;
    ground.rotation.set(0, 0, 0);
    ground.scale.set(1, 1, 1);
    ground.castShadow = false;
    ground.receiveShadow = true;
    ground.visible = true;

    const material = ground.material as MeshStandardMaterial;
    material.color.setRGB(0.9, 0.9, 0.9);
    material.roughness = 1.0;
    material.metalness = 0.0;
    material.side = FrontSide;
    material.needsUpdate = true;

    return true;
  };

  const setupGroundGeometry = (
    objectParts: ObjectPartThree[],
  ): Mesh | undefined => {
    const box = getObjectsBox(objectParts.map((p) => p.root));
    if (!box) return undefined;

    const center = new ThreeVector3();
    const size = new ThreeVector3();

    box.getCenter(center);
    box.getSize(size);

    const largestSide = Math.max(size.x, size.z, 1);
    const groundSide = largestSide * 70;

    const geometry = new PlaneGeometry(groundSide, groundSide, 32, 32);
    geometry.rotateX(-Math.PI / 2);

    // const material = new MeshStandardMaterial({
    //   color: new Color(0.9, 0.9, 0.9),
    //   roughness: 1.0,
    //   metalness: 0.0,
    //   side: FrontSide,
    // });
    const material = new ShadowMaterial({
      color: new Color(0x000000),
      opacity: 0.18,
      side: FrontSide,
    });

    const ground = new Mesh(geometry, material);
    ground.name = 'myGround';
    ground.position.copy(center);
    ground.position.y -= size.y / 2.0;
    ground.receiveShadow = true;

    return ground;
  };

  const setupCeilingGeometry = (
    objectParts: ObjectPartThree[],
  ): Mesh | undefined => {
    const box = getObjectsBox(objectParts.map((p) => p.root));
    if (!box) return undefined;

    const center = new ThreeVector3();
    const size = new ThreeVector3();

    box.getCenter(center);
    box.getSize(size);

    const largestSide = Math.max(size.x, size.z, 1);
    const ceilingSide = largestSide * 270;

    const geometry = new PlaneGeometry(ceilingSide, ceilingSide, 32, 32);
    geometry.rotateX(Math.PI / 2);

    const material = new MeshStandardMaterial({
      color: new Color(1, 1, 1),
      roughness: 1.0,
      metalness: 0.0,
      side: DoubleSide,
    });

    const ceiling = new Mesh(geometry, material);
    ceiling.name = 'myCeiling';
    ceiling.position.copy(center);
    ceiling.position.y += size.y * 9;
    ceiling.receiveShadow = false;

    return ceiling;
  };

  const setupWallGeometry = (
    objectParts: ObjectPartThree[],
  ): Mesh | undefined => {
    const box = getObjectsBox(objectParts.map((p) => p.root));
    if (!box) return undefined;

    const center = new ThreeVector3();
    const size = new ThreeVector3();

    box.getCenter(center);
    box.getSize(size);

    const largestSide = Math.max(size.x, size.z, 1);
    const wallSide = largestSide * 90;

    const geometry = new PlaneGeometry(wallSide, wallSide, 1, 1);
    const material = new ShadowMaterial({
      color: new Color(0xffffff),
      opacity: 0.02,
      side: DoubleSide,
    });

    const wall = new Mesh(geometry, material);
    wall.name = 'wall';
    wall.position.copy(center);
    wall.position.z -= wallSide / 2;
    wall.receiveShadow = false;

    return wall;
  };

  const isObjectPartVisibleInComposition = (
    sceneObject: ObjectPartThree,
    targetComposition: TreeOptionSelections,
  ): boolean => {
    if (sceneObject.groupID === -1 && sceneObject.optionID === -1) {
      return true;
    }

    return targetComposition.some(
      (compositionPart) => compositionPart.groupID === sceneObject.groupID
        && compositionPart.optionID === sceneObject.optionID,
    );
  };

  const getVisibleObjectPartsForComposition = (
    objectParts: ObjectPartThree[],
    targetComposition: TreeOptionSelections,
  ): ObjectPartThree[] => objectParts.filter(
    (obj) => isObjectPartVisibleInComposition(obj, targetComposition),
  );

  const removeOrphanHelperObjects = (targetScene: Scene): void => {
    const helperNames = new Set(['myGround', 'myCeiling', 'wall']);

    targetScene.children
      .filter((obj) => helperNames.has(obj.name) && obj !== groundRef.current)
      .forEach((obj) => {
        obj.parent?.remove(obj);
        disposeObject3D(obj);
      });
  };

  const replaceMeshGeometry = (target: Mesh, source: Mesh): void => {
    target.geometry.dispose();
    target.geometry = source.geometry;
    target.position.copy(source.position);
    target.rotation.copy(source.rotation);
    target.scale.copy(source.scale);
    target.receiveShadow = source.receiveShadow;

    disposeMaterial(source.material);
  };

  const refreshHelperObjectsForCurrentComposition = (
    objectParts: ObjectPartThree[],
  ): void => {
    const targetScene = sceneRef.current;
    if (!targetScene) return;

    const visibleObjects = getVisibleObjectPartsForComposition(objectParts, composition);
    if (visibleObjects.length === 0) return;

    removeOrphanHelperObjects(targetScene);

    let currentGround = groundRef.current;

    if (!currentGround || !currentGround.parent) {
      currentGround = createReusableGroundMesh();
      targetScene.add(currentGround);
      groundRef.current = currentGround;
      helperObjectsRef.current = [currentGround];
    }

    if (!updateReusableGroundMesh(currentGround, visibleObjects)) {
      return;
    }

    setupLightsPosition(visibleObjects);
    invalidateNowAndNextFrame();
  };

  const showObjectComposition = (
    sceneObjectsToConsider: ObjectPartThree[],
  ): void => {
    if (sceneObjectsToConsider.length === 0) return;

    sceneObjectsToConsider.forEach((sceneObject) => {
      sceneObject.root.visible = isObjectPartVisibleInComposition(sceneObject, composition);
    });

    invalidateNowAndNextFrame();
  };

  const disposeObjectParts = (objectParts: ObjectPartThree[]) => {
    objectParts.forEach((sceneObject) => {
      sceneObject.root.parent?.remove(sceneObject.root);
      disposeObject3D(sceneObject.root);
    });
  };

  const loadObjectToScene = (): void => {
    hasInitialCameraFitRef.current = false;
    const targetScene = sceneRef.current;
    if (!targetScene) return;

    disposeObjectParts(sceneObjectsRef.current);
    clearHelperObjects();
    setSceneObjects([]);

    const arrLoadObjectsOperationsImmediate: Array<() => Promise<ObjectPartThree>> = [];
    const arrLoadObjectsOperationsDeferred: DeferredObjectLoad[] = [];

    if (sltxFile.isOpened() === false) {
      return;
    }

    let gltfFilePath: string = sltxFile.getCompletePath(
      sltxFile.getRootFileContents().objects,
    );

    let [dirPath, fileName] = FilePaths.splitFileFromPath(gltfFilePath);

    if (fileName.length > 0) {
      const localRemainDirPath = dirPath;
      const localRemainFileName = fileName;
      const localRemainGroupID = -1;
      const localRemainOptionID = -1;

      arrLoadObjectsOperationsImmediate.push(
        () => ObjectLoaderThree(
          localRemainDirPath,
          localRemainFileName,
          localRemainGroupID,
          localRemainOptionID,
        ),
      );
    }

    const modelGroups = sltxFile.getRootFileContents().groups;
    if (modelGroups) {
      modelGroups.forEach((group) => {
        const groupOptions = ModelGroupInfo.getGroupAllOptions(group);
        groupOptions.forEach((groupOption) => {
          gltfFilePath = sltxFile.getCompletePath(groupOption.objectRef);
          [dirPath, fileName] = FilePaths.splitFileFromPath(gltfFilePath);

          const partNeededImmediately = composition.some((entry) => (
            entry.groupID === group.id
            && entry.optionID === groupOption.id
          ));

          const localDirPath = dirPath;
          const localFileName = fileName;
          const localGroupID = group.id;
          const localOptionID = groupOption.id;

          if (localFileName) {
            const fncLoadPart = () => ObjectLoaderThree(
              localDirPath,
              localFileName,
              localGroupID,
              localOptionID,
            );

            if (partNeededImmediately) {
              arrLoadObjectsOperationsImmediate.push(fncLoadPart);
            } else {
              arrLoadObjectsOperationsDeferred.push({
                groupId: group.id,
                optionId: groupOption.id,
                loadObjectPromise: fncLoadPart,
              });
            }
          }
        });
      });
    }

    setObjectsToLoad([...arrLoadObjectsOperationsDeferred]);
    setIsLoaded(false);

    Promise.all(arrLoadObjectsOperationsImmediate.map((f) => f()))
      .then((arrResults) => {
        const loadedSceneObjects = [...arrResults];

        loadedSceneObjects.forEach((obj) => targetScene.add(obj.root));

        const visibleLoadedObjects = getVisibleObjectPartsForComposition(
          loadedSceneObjects,
          composition,
        );

        setupCameraPositionAndMovement(
          visibleLoadedObjects,
          cameraPosition,
        );

        setSceneObjects(loadedSceneObjects);
        showObjectComposition(loadedSceneObjects);
        refreshHelperObjectsForCurrentComposition(loadedSceneObjects);

        invalidateNowAndNextFrame();

        const wereObjectsLoaded = loadedSceneObjects.every((obj) => obj.root !== undefined && obj.root !== null);
        if (wereObjectsLoaded) setIsLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load Three.js scene objects', err);
        setIsLoaded(false);
      });
  };

  useEffect(() => {
    let objectLoaders: Array<() => Promise<ObjectPartThree>> = [];
    let missingObjects: DeferredObjectLoad[] = [];

    const newSelection: TreeOptionSelection[] = [];

    if (previousSelection) {
      for (let i = 0; i < composition.length; i += 1) {
        const treeNodes = previousSelection.filter(
          (sn) => sn.groupID === composition[i].groupID
            && sn.optionID !== composition[i].optionID,
        );

        treeNodes.forEach((tn) => newSelection.push(tn));
      }
    }

    composition.forEach((c) => {
      const mObjects = objectsToLoad.filter(
        (object) => object.groupId === c.groupID && object.optionId === c.optionID,
      );

      objectLoaders = objectLoaders.concat(mObjects.map((obj) => obj.loadObjectPromise));
      missingObjects = missingObjects.concat(mObjects);
    });

    if (objectLoaders.length > 0) {
      setIsLoadingPart(true);

      Promise.all(objectLoaders.map((f) => f()))
        .then((arrResultsDeferred) => {
          const targetScene = sceneRef.current;
          if (!targetScene) return;

          arrResultsDeferred.forEach((obj) => targetScene.add(obj.root));

          const allObjects = [...sceneObjects, ...arrResultsDeferred];

          const remainObjectsToLoad = objectsToLoad.filter(
            (mo1) => !missingObjects.some(
              (mo2) => mo2.groupId === mo1.groupId && mo2.optionId === mo1.optionId,
            ),
          );

          setObjectsToLoad(remainObjectsToLoad);
          setSceneObjects(allObjects);
          showObjectComposition(allObjects);
          refreshHelperObjectsForCurrentComposition(allObjects);
          invalidateNowAndNextFrame();
        })
        .catch((err) => {
          console.error('Failed to load deferred Three.js scene objects', err);
        })
        .finally(() => {
          setIsLoadingPart(false);
        });

      return;
    }

    showObjectComposition(sceneObjects);
    refreshHelperObjectsForCurrentComposition(sceneObjects);
    setIsLoadingPart(false);
    setPreviousSelection(composition);
  }, [composition, sceneObjects, screenshotId]);

  const getModelIlluminationData = (): ModelIlluminationData | undefined => {
    const currentScene = sceneRef.current;
    if (!currentScene) return undefined;

    const currentModelIlluminationData = new ModelIlluminationData();
    currentModelIlluminationData.environmentIntensity = modelIlluminationData.environmentIntensity;

    lightsRef.current.forEach((light) => {
      const newSceneLight = new SceneLightData();

      const direction = new ThreeVector3()
        .subVectors(light.target.position, light.position)
        .normalize();

      newSceneLight.name = light.name;
      newSceneLight.direction = new Vector(
        direction.x,
        direction.y,
        direction.z,
      );

      newSceneLight.position = new Vector(
        light.position.x,
        light.position.y,
        light.position.z,
      );

      newSceneLight.intensity = light.intensity;
      newSceneLight.castShadows = light.castShadow;

      // Three does not have ShadowGenerator darkness like Babylon.
      // Preserve the original value if the light exists in the original config.
      const originalLight = modelIlluminationData.lights.find((l) => l.name === light.name);
      newSceneLight.shadowDarkness = originalLight?.shadowDarkness ?? 0;

      currentModelIlluminationData.lights.push(newSceneLight);
    });

    return currentModelIlluminationData;
  };

  const takeScreenShot = (fileId: string): void => {
    const renderer = rendererRef.current;
    const targetScene = sceneRef.current;
    const camera = cameraRef.current;

    if (!renderer || !targetScene || !camera) return;

    renderer.render(targetScene, camera);
    const dataUrl = renderer.domElement.toDataURL('image/png');
    setScreenshotHandler(fileId, dataUrl);
  };

  useEffect(() => {
    if (sceneObjects.length === 0) {
      return undefined;
    }

    if (hasInitialCameraFitRef.current) {
      return undefined;
    }

    if (!cameraRef.current || !controlsRef.current || !rendererRef.current) {
      return undefined;
    }

    let raf2 = 0;

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const visibleObjects = getVisibleObjectPartsForComposition(sceneObjects, composition);
        setupCameraPositionAndMovement(visibleObjects, cameraPosition);
        hasInitialCameraFitRef.current = true;
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [sceneObjects]);

  useEffect(() => {
    if (!actualScreenShotId) {
      return undefined;
    }

    if (cameraRef.current) {
      const visibleObjects = getVisibleObjectPartsForComposition(sceneObjects, composition);
      setupCameraPositionAndMovement(visibleObjects, new BabylonVector3(0, 0, 1));
      invalidateRenderRef.current();
    }

    const timer = window.setTimeout(() => {
      takeScreenShot(actualScreenShotId);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [actualScreenShotId]);

  useEffect(() => {
    setActualScreenShotId(screenshotId);
  }, [secondaryScreenShotId]);

  useEffect(() => {
    setSecondaryScreenShotId(screenshotId);
  }, [screenshotId]);

  useEffect(() => {
    if (cameraRef.current) {
      const visibleObjects = getVisibleObjectPartsForComposition(sceneObjects, composition);
      setupCameraPositionAndMovement(visibleObjects, cameraPosition);
      invalidateRenderRef.current();
    }
  }, [cameraPosition]);

  const onSceneReady = ({
    renderer,
    scene,
    camera,
    controls,
    invalidateRender,
  }: ThreeComponentSceneReadyArgs) => {
    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;
    controlsRef.current = controls;
    invalidateRenderRef.current = invalidateRender;

    setupLightSources(scene);
    scene.background = new Color(0xffffff);
    loadObjectToScene();
    invalidateNowAndNextFrame();

    invalidateRenderRef.current();
    setPreviousSelection(composition);

    if (registerCallbackGetModelIlluminationData) {
      registerCallbackGetModelIlluminationData(
        (): ModelIlluminationData | undefined => getModelIlluminationData(),
      );
    }
  };

  return (
    <div ref={sceneContainerRef} style={{ width: '100%', height: '100%' }}>
      <ThreeComponentScene callbackOnSceneReady={onSceneReady} />
    </div>
  );
}

function getObjectsBox(objects: Object3D[]): Box3 | undefined {
  const box = new Box3();
  let hasObject = false;

  objects.forEach((obj) => {
    if (!obj) return;

    const objectBox = new Box3().setFromObject(obj);
    if (objectBox.isEmpty()) return;

    box.union(objectBox);
    hasObject = true;
  });

  return hasObject ? box : undefined;
}

function fitCameraToObjects(
  camera: PerspectiveCamera,
  controls: OrbitControls,
  objects: Object3D[],
  viewDirection: ThreeVector3,
  canvasWidth: number,
  canvasHeight: number,
): void {
  const box = getObjectsBox(objects);
  if (!box) return;

  const center = new ThreeVector3();
  const size = new ThreeVector3();

  box.getCenter(center);
  box.getSize(size);

  const refValue = Math.max(size.x, size.y, size.z, 0.001);

  camera.near = Math.max(0.01, refValue * 0.01);
  camera.far = Math.max(1000, refValue * 100);
  camera.updateProjectionMatrix();

  const dir = viewDirection.clone();
  if (dir.lengthSq() < 1e-6) {
    dir.set(0, 0, 1);
  }
  dir.normalize();

  const aspect = canvasWidth / Math.max(canvasHeight, 1);
  const vFov = (camera.fov * Math.PI) / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);

  let worldUp = new ThreeVector3(0, 1, 0);
  if (Math.abs(dir.dot(worldUp)) > 0.999) {
    worldUp = new ThreeVector3(0, 0, 1);
  }

  const right = new ThreeVector3().crossVectors(worldUp, dir).normalize();
  const up = new ThreeVector3().crossVectors(dir, right).normalize();

  const corners = [
    new ThreeVector3(box.min.x, box.min.y, box.min.z),
    new ThreeVector3(box.min.x, box.min.y, box.max.z),
    new ThreeVector3(box.min.x, box.max.y, box.min.z),
    new ThreeVector3(box.min.x, box.max.y, box.max.z),
    new ThreeVector3(box.max.x, box.min.y, box.min.z),
    new ThreeVector3(box.max.x, box.min.y, box.max.z),
    new ThreeVector3(box.max.x, box.max.y, box.min.z),
    new ThreeVector3(box.max.x, box.max.y, box.max.z),
  ];

  const tanH = Math.tan(hFov / 2);
  const tanV = Math.tan(vFov / 2);

  let requiredDistance = 0;

  corners.forEach((corner) => {
    const local = corner.clone().sub(center);

    const x = Math.abs(local.dot(right));
    const y = Math.abs(local.dot(up));
    const z = local.dot(dir);

    requiredDistance = Math.max(
      requiredDistance,
      x / Math.max(tanH, 1e-6) + z,
      y / Math.max(tanV, 1e-6) + z,
    );
  });

  const radius = Math.max(requiredDistance * 1.12, refValue * 0.1);

  camera.position.copy(center.clone().add(dir.multiplyScalar(radius)));
  camera.lookAt(center);

  controls.target.copy(center);
  controls.minDistance = refValue * 0.05;
  controls.maxDistance = refValue * 10;
  controls.update();
}

function disposeMaterial(material: Mesh['material']): void {
  if (Array.isArray(material)) {
    material.forEach((m) => m.dispose());
  } else if (material) {
    material.dispose();
  }
}

function disposeObject3D(object: Object3D): void {
  object.traverse((child) => {
    const mesh = child as Mesh;

    if (mesh.geometry) {
      mesh.geometry.dispose();
    }

    disposeMaterial(mesh.material);
  });
}
